import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

interface DashboardStats {
  total_tandas: number;
  active_tandas: number;
  total_contributions: number;
  upcoming_payments: number;
  completed_tandas: number;
}

interface Tanda {
  id: number;
  name: string;
  status: string;
  current_members: number;
  member_count: number;
  contribution_amount: number;
  total_amount: number;
  frequency: string;
  start_date: string;
}

interface Payment {
  id: number;
  amount: number;
  status: string;
  payment_date: string | null;
  round_id: number;
  user_id: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activeTandas, setActiveTandas] = useState<Tanda[]>([]);
  const [upcomingPayments, setUpcomingPayments] = useState<Payment[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/dashboard');
      setStats(response.data.stats);
      setActiveTandas(response.data.active_tandas);
      setUpcomingPayments(response.data.upcoming_payments);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      enqueueSnackbar('Failed to load dashboard data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'forming': return 'warning';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'late': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="lg">
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('dashboard.welcomeBack')} {user?.full_name}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('dashboard.subtitle')}
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GroupsIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats?.total_tandas || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.totalTandas')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats?.active_tandas || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.activeTandas')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WalletIcon color="warning" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4">${stats?.total_contributions || 0} MXN</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.totalContributions')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon color="info" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats?.upcoming_payments || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.upcomingPayments')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Active Tandas */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                {t('dashboard.activeTandasTitle')} ({activeTandas.length})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate('/create-tanda')}
              >
                {t('dashboard.createNew')}
              </Button>
            </Box>

            {activeTandas.length === 0 ? (
              <Alert severity="info">
                {t('dashboard.noActiveTandas')}
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {activeTandas.map((tanda) => (
                  <Grid item xs={12} sm={6} key={tanda.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 2,
                          borderColor: 'primary.main',
                        },
                      }}
                      onClick={() => navigate(`/tandas/${tanda.id}`)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" component="div">
                            {tanda.name}
                          </Typography>
                          <Chip
                            label={tanda.status.toUpperCase()}
                            size="small"
                            color={getStatusColor(tanda.status) as any}
                          />
                        </Box>

                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('dashboard.members')} {tanda.current_members}/{tanda.member_count}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('dashboard.contribution')}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            ${tanda.contribution_amount} MXN
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('dashboard.total')}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            ${tanda.total_amount} MXN
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('dashboard.started')} {format(new Date(tanda.start_date), 'MMM dd')}
                          </Typography>
                          <Button
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/tandas/${tanda.id}`);
                            }}
                          >
                            {t('dashboard.view')}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>

          {/* Quick Actions */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.quickActions')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/create-tanda')}
                >
                  {t('dashboard.createTanda')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<GroupsIcon />}
                  onClick={() => navigate('/tandas')}
                >
                  {t('dashboard.browseTandas')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<MoneyIcon />}
                  onClick={() => navigate('/profile')}
                >
                  {t('dashboard.makePayment')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<HistoryIcon />}
                  onClick={() => navigate('/profile')}
                >
                  {t('dashboard.viewHistory')}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Column - Upcoming Payments & Recent Activity */}
        <Grid item xs={12} md={4}>
          {/* Upcoming Payments */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.upcomingPaymentsTitle')}
            </Typography>

            {upcomingPayments.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('dashboard.noUpcomingPayments')}
              </Alert>
            ) : (
              <List>
                {upcomingPayments.slice(0, 5).map((payment) => (
                  <ListItem
                    key={payment.id}
                    secondaryAction={
                      <Chip
                        label={payment.status}
                        size="small"
                        color={getPaymentStatusColor(payment.status) as any}
                      />
                    }
                  >
                    <ListItemIcon>
                      <MoneyIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Payment - $${payment.amount} MXN`}
                      secondary={
                        payment.payment_date
                          ? `Due: ${format(new Date(payment.payment_date), 'MMM dd')}`
                          : 'No due date'
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {upcomingPayments.length > 5 && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  size="small"
                  onClick={() => navigate('/profile')}
                >
                  {t('dashboard.viewAllPayments')}
                </Button>
              </Box>
            )}
          </Paper>

          {/* Recent Activity */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.recentActivity')}
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={t('dashboard.paymentReceived')}
                  secondary="John Doe paid $100 for Family Tanda"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <GroupsIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('dashboard.newMember')}
                  secondary="Jane Smith joined Vacation Fund Tanda"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <WarningIcon color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary={t('dashboard.paymentReminder')}
                  secondary="Payment due in 2 days for Home Renovation Tanda"
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={t('dashboard.tandaCompleted')}
                  secondary="Emergency Fund Tanda successfully completed"
                />
              </ListItem>
            </List>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                size="small"
                onClick={() => navigate('/profile')}
              >
                {t('dashboard.viewAllActivity')}
              </Button>
            </Box>
          </Paper>

          {/* Tips Card */}
          <Paper sx={{ p: 3, mt: 3, backgroundColor: 'info.light', color: 'info.contrastText' }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.tips')}
            </Typography>
            <Typography variant="body2">
              {t('dashboard.tip1')}
              <br />
              {t('dashboard.tip2')}
              <br />
              {t('dashboard.tip3')}
              <br />
              {t('dashboard.tip4')}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Empty State for No Tandas */}
      {stats?.total_tandas === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', mt: 4 }}>
          <GroupsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {t('dashboard.welcomeTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {t('dashboard.welcomeText')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/create-tanda')}
            >
              {t('dashboard.createFirst')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<GroupsIcon />}
              onClick={() => navigate('/tandas')}
            >
              {t('dashboard.browse')}
            </Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default Dashboard;
